<template>
  <div class="inquiry">
    <nav-component></nav-component>

    <section class="inquiry__hero">
      <div class="rt-inner inquiry__hero-inner">
        <span class="rt-eyebrow inquiry__eyebrow">CONTACT</span>
        <h1 class="inquiry__title">お問い合わせ</h1>
        <p class="inquiry__lead">
          サービスのご相談・デモのご希望など、お気軽にどうぞ。
        </p>
      </div>
    </section>

    <section class="inquiry__form-wrap">
      <div class="rt-inner">
        <div class="inquiry__embed-wrap">
          <p v-if="status === 'loading'" class="inquiry__status" role="status">フォームを読み込み中です…</p>
          <p v-if="status === 'failed'" class="inquiry__status inquiry__status--failed" role="alert">
            フォームを表示できませんでした。
            <a :href="pageUrl" class="inquiry__fallback-link" rel="noopener">こちらの問い合わせページ</a>からお問い合わせください。
          </p>
          <div ref="embed" class="inquiry__embed"></div>
        </div>
      </div>
    </section>

    <site-footer></site-footer>
  </div>
</template>

<script>
import { defineComponent, onMounted, onBeforeUnmount, ref } from 'vue'
import navComponent from '../components/Nav.vue'
import SiteFooter from '../components/SiteFooter.vue'
import { loadInquiryWidget } from '../lib/inquiryWidget'

export default defineComponent({
  name: 'InquiryView',
  components: { navComponent, SiteFooter },
  setup () {
    const pageUrl = process.env.VUE_APP_MONSTRS_INQUIRY_PAGE_URL
    const embed = ref(null)
    const status = ref('loading') // loading | ready | failed
    let app = null
    let disposed = false

    onMounted(async () => {
      try {
        const widget = await loadInquiryWidget(process.env.VUE_APP_MONSTRS_INQUIRY_WIDGET_URL)
        // 読み込み待ちの間にページを離れた、または既にマウント済みなら何もしない
        if (disposed || app) return
        app = widget.mount(embed.value)
        status.value = 'ready'
      } catch (error) {
        if (disposed) return
        app = null
        status.value = 'failed'
      }
    })

    onBeforeUnmount(() => {
      disposed = true
      if (app) {
        const mounted = app
        app = null
        mounted.unmount()
      }
    })

    return { embed, status, pageUrl }
  }
})
</script>

<style scoped>
.inquiry__hero {
  position: relative;
  overflow: hidden;
  padding: 116px 24px 24px;
  background: radial-gradient(circle at 85% 20%, rgba(238, 134, 168, 0.18), transparent 45%);
}

.inquiry__hero-inner {
  text-align: left;
}

.inquiry__eyebrow {
  color: var(--brand-pink);
  background: var(--pink-bg);
}

.inquiry__title {
  font-size: clamp(26px, 5vw, 34px);
  font-weight: 900;
  color: var(--ink);
}

.inquiry__lead {
  font-size: 14px;
  line-height: 1.9;
  color: var(--ink-soft);
  margin-top: 10px;
  max-width: 440px;
}

.inquiry__form-wrap {
  padding: 24px 24px 64px;
}

.inquiry__embed-wrap {
  max-width: 720px;
  margin: 0 auto;
  text-align: left;
}

.inquiry__status {
  font-size: 14px;
  line-height: 1.9;
  color: var(--ink-soft);
}

.inquiry__fallback-link {
  color: var(--brand-pink);
  font-weight: 700;
  text-decoration: underline;
}

/* monstrs ウィジェット（Bootstrap のクラス）への見た目。すべて埋め込み領域を起点にする */
.inquiry__embed {
  max-width: 100%;
  overflow-wrap: anywhere;
  font-size: 14px;
  line-height: 1.8;
  color: var(--ink);
}

.inquiry__embed :deep(*),
.inquiry__embed :deep(*::before),
.inquiry__embed :deep(*::after) {
  box-sizing: border-box;
}

/* ウィジェット自身の見出しはヒーローと重複するため隠す */
.inquiry__embed :deep(h1) {
  display: none;
}

.inquiry__embed :deep(.col-12) {
  width: 100%;
  max-width: 100%;
}

.inquiry__embed :deep(.mb-3) {
  margin-bottom: 18px;
}

.inquiry__embed :deep(.form-label) {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-soft);
  margin-bottom: 7px;
}

/* Bootstrap の floating label は input → label の順。ラベルを入力欄の上に表示する */
.inquiry__embed :deep(.form-floating) {
  display: flex;
  flex-direction: column;
}

/* 見た目の順序は label → 入力欄 → 補足（form-text など） */
.inquiry__embed :deep(.form-floating > *) {
  order: 3;
}

.inquiry__embed :deep(.form-floating > .form-control) {
  order: 2;
}

.inquiry__embed :deep(.form-floating > label) {
  order: 1;
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-soft);
  margin-bottom: 7px;
}

.inquiry__embed :deep(.d-flex) {
  display: flex;
}

.inquiry__embed :deep(.justify-content-end) {
  justify-content: flex-end;
}

.inquiry__embed :deep(.form-control) {
  display: block;
  width: 100%;
  max-width: 100%;
  border: 1.5px solid var(--line-input);
  border-radius: 12px;
  padding: 11px 14px;
  font-size: 14px;
  font-family: var(--font-head);
  color: var(--ink);
  background: var(--surface);
  transition: border-color 0.15s;
}

.inquiry__embed :deep(.form-control:focus) {
  outline: none;
  border-color: var(--brand-pink);
}

.inquiry__embed :deep(textarea.form-control) {
  resize: vertical;
}

.inquiry__embed :deep(.form-control.is-invalid) {
  border-color: var(--brand-red);
}

.inquiry__embed :deep(.form-check) {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.inquiry__embed :deep(.form-check-input) {
  flex: none;
  margin-top: 5px;
  accent-color: var(--brand-pink);
}

.inquiry__embed :deep(.form-check-label) {
  font-size: 14px;
  color: var(--ink);
  cursor: pointer;
}

.inquiry__embed :deep(.form-text) {
  display: block;
  font-size: 12px;
  line-height: 1.7;
  color: var(--ink-soft);
  margin-top: 6px;
}

.inquiry__embed :deep(.text-danger),
.inquiry__embed :deep(.invalid-feedback) {
  font-size: 12px;
  color: var(--brand-red);
}

.inquiry__embed :deep(ol),
.inquiry__embed :deep(ul) {
  padding-left: 1.4em;
  margin: 6px 0;
}

.inquiry__embed :deep(.alert) {
  padding: 12px 16px;
  margin-bottom: 18px;
  border: 1.5px solid var(--line-input);
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.8;
  background: var(--surface-soft);
  color: var(--ink);
}

.inquiry__embed :deep(.alert-success) {
  border-color: var(--brand-green);
}

.inquiry__embed :deep(.alert-warning) {
  border-color: var(--brand-pink);
}

.inquiry__embed :deep(.alert-danger) {
  border-color: var(--brand-red);
}

.inquiry__embed :deep(.alert-info) {
  border-color: var(--brand-blue);
}

.inquiry__embed :deep(.btn) {
  display: inline-block;
  border: 0;
  cursor: pointer;
  font-family: var(--font-head);
  font-weight: 700;
}

.inquiry__embed :deep(.btn-primary),
.inquiry__embed :deep(.btn-lg) {
  font-size: 16px;
  padding: 14px 48px;
  color: #fff;
  background: var(--brand-pink);
  border-radius: var(--radius-pill);
}

.inquiry__embed :deep(.btn-primary:disabled) {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
